import { Button, Field, Wordmark } from '@gramas/ui'
import { useState } from 'react'
import { ApiError, login } from '../api/client.js'
import { GlitimeMark } from '../glitime/Mark.js'
import { sassLine } from '../sass/engine.js'
import { useSassOnce } from '../sass/useSassOnce.js'

export function Login({ onSignedIn }: { onSignedIn: () => void }) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // Drawn once per mount, not per render: every keystroke re-renders this
  // form, and a greeting that reshuffles under the fingers is a slot machine.
  const [greeting] = useState(() => sassLine('login'))
  // Same rule as the greeting, but per attempt rather than per mount: `busy`
  // only flips true once per submit, so this draws once per attempt too.
  const busyLabel =
    useSassOnce(
      busy ? 'busy' : null,
      () => sassLine('loading', { surface: 'login' }) ?? 'Jungiamasi…',
    ) ?? 'Prisijungti'

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await login(email, password)
      onSignedIn()
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 401
          ? (sassLine('error_login_password', {}) ?? 'Neteisingas el. paštas arba slaptažodis')
          : (sassLine('error_login', {}) ?? 'Nepavyko prisijungti'),
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex h-full flex-col justify-center px-6">
      <div className="mx-auto w-full max-w-sm">
        {/* Static: the boot splash has just played the animated one, and
            playing it twice in two seconds would read as a stutter. */}
        <h1>
          <Wordmark size={26} />
        </h1>
        <p className="mt-2 text-sm text-mute">Mitybos ir treniruočių dienynas</p>
        {greeting != null && (
          /* A cameo on a screen she otherwise has no business being on —
             before anyone has signed in. Used to appear only on the five
             days the app wears the day; she is signing every greeting now,
             big day or not. */
          <p className="mt-5 flex items-center gap-2.5 text-dim text-sm">
            <GlitimeMark size={20} alive />
            <span>{greeting}</span>
          </p>
        )}

        <form onSubmit={submit} className="mt-8 flex flex-col gap-4">
          <Field
            label="El. paštas"
            type="email"
            autoComplete="username"
            inputMode="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <Field
            label="Slaptažodis"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={error}
            required
          />
          <Button type="submit" variant="primary" size="lg" block disabled={busy}>
            {busyLabel}
          </Button>
        </form>
      </div>
    </div>
  )
}
